/**
 * AppGate is the top-level route guard and bootstrapper for the application.
 *
 * Responsibilities:
 * - Acts as a single entry point for all routes.
 * - Applies route-level access rules based on route metadata (`auth`, `team`).
 * - Enforces authentication for protected routes.
 * - Whenever a token is present, blocks rendering on ALL routes until the
 *   combined auth state (user + session) is loaded. This guarantees that no
 *   route — public or protected — ever renders with an unknown MFA decision.
 * - Enforces MFA: when the current session has `mfa_required: true`, locks the
 *   user on `/verify-mfa` until verification completes. `/unlock` is the only
 *   route exempt from this lock so a locked-out user can still recover.
 * - Handles impersonation tokens that may arrive via cookies.
 * - Enforces team selection for routes that require an active team.
 *
 * Behavior:
 * - Public routes render immediately when there is no token.
 * - Protected routes require a valid auth token; otherwise the user is redirected to login.
 * - MFA redirects are performed synchronously via `<Navigate>` in render
 *   (not in an effect) so the destination never flashes the source route's
 *   content before the redirect fires.
 * - Routes marked with `team: "required"` redirect to Select Team if the user has no `current_team_id`.
 * - Team state is derived from `user.current_team_id` inside TeamProvider,
 *   so AppGate does not manually load or reset team data.
 *
 * AppGate intentionally centralizes navigation side effects and blocking UI logic
 * (including MFA gating) to keep individual route components simple and free of
 * auth/MFA/team bootstrap concerns.
 */
import { useEffect } from "react";
import {
  Navigate,
  Outlet,
  useLocation,
  useMatch,
  useMatches,
  useNavigate,
} from "react-router-dom";

import Button from "~/components/ui/button/Button";
import { CenteredMessage } from "~/components/ui/centeredMessage/CenteredMessage";
import FullscreenLoader from "~/components/ui/loaders/FullscreenLoader";
import { useAuth } from "~/contexts/AuthProvider";
import { useImpersonation } from "~/hooks/useImpersonation";
import { PATTERNS, routes } from "~/router";
import { getWhiteLabelConfig } from "~/utils/white-label/getWhiteLabelConfig";

type RouteMeta = {
  auth?: "public" | "protected";
  team?: "required" | "optional";
};

function mergeRouteMeta(matches: ReturnType<typeof useMatches>): RouteMeta {
  return matches.reduce<RouteMeta>(
    (acc, m) => Object.assign(acc, (m.handle ?? {}) as RouteMeta),
    {},
  );
}

// `Navigate`'s `state` is typed as `unknown`. Defensively validate the
// captured `from` location before using it as a post-verify redirect target:
//   - must be a Location-shaped object,
//   - pathname must be a relative path starting with `/` (never absolute /
//     scheme-qualified — would otherwise be an open-redirect vector),
//   - pathname must not be `/verify-mfa` or `/unlock` (would loop or be
//     meaningless after verify).
function readSafeFrom(
  state: unknown,
): { pathname: string; search: string } | null {
  if (!state || typeof state !== "object") return null;
  const from = (state as { from?: unknown }).from;
  if (!from || typeof from !== "object") return null;
  const { pathname, search } = from as {
    pathname?: unknown;
    search?: unknown;
  };
  if (typeof pathname !== "string") return null;
  // A leading `//` is treated by URL parsers as a protocol-relative URL,
  // which would let an attacker craft `state.from` to escape the SPA.
  if (!pathname.startsWith("/") || pathname.startsWith("//")) return null;
  if (
    pathname === PATTERNS.verifyMfa ||
    pathname === PATTERNS.unlock ||
    pathname === PATTERNS.demoLogin
  ) {
    return null;
  }

  return {
    pathname,
    search: typeof search === "string" ? search : "",
  };
}

export function AppGate() {
  const matches = useMatches();
  const meta = mergeRouteMeta(matches);
  const nav = useNavigate();
  const location = useLocation();

  const {
    token,
    user,
    mfaRequired,
    isLoading: isAuthLoading,
    isError: isAuthError,
    clearAuth,
    ssoStatus,
  } = useAuth();
  const { consumeImpersonationCookie } = useImpersonation();
  const { alliaiapp } = getWhiteLabelConfig();
  const isOnVerifyMfa = useMatch(PATTERNS.verifyMfa) !== null;
  // `/unlock` must remain reachable even when the session's `mfa_required`
  // flag is true — a locked-out user with a stale-but-valid token in
  // localStorage still needs to complete the unlock flow before anything else.
  const isOnUnlock = useMatch(PATTERNS.unlock) !== null;
  // `/demo-login` mirrors `/unlock`: the emailed one-time link must work
  // regardless of whatever session is already in localStorage.
  const isOnDemoLogin = useMatch(PATTERNS.demoLogin) !== null;
  // `/activate/:code` is an emailed link too. It is exempt from the MFA lock
  // only, NOT MFA-sensitive: blocking it while the new session loads would
  // unmount the page mid-activation and lose its post-signup redirect.
  const isOnActivateAccount = useMatch(PATTERNS.activateAccount) !== null;

  // Impersonation cookie may appear on any route — handle it once at app start.
  useEffect(() => {
    consumeImpersonationCookie();
  }, [consumeImpersonationCookie]);

  if (alliaiapp && !token && ssoStatus === "denied") {
    return (
      <CenteredMessage
        title="Authorization Error"
        description="We were unable to authorize your request."
      />
    );
  }

  // Recoverable error path: if either auth query (user/session) hard-failed
  // (5xx / network), we can't decide MFA state and the loader would spin
  // forever. Render an error screen with an explicit Sign Out so the user
  // can drop the token and try again instead of being stuck. 401/403 are
  // handled inline by `handleInvalidToken` and never land here.
  if (!!token && isAuthError) {
    const onSignOut = () => {
      clearAuth();
      nav(routes.auth.login(), { replace: true });
    };

    return (
      <CenteredMessage
        title="Something went wrong"
        description="We couldn't verify your session. Please sign out and try again."
      >
        <Button onClick={onSignOut}>Sign out</Button>
      </CenteredMessage>
    );
  }

  // Routes whose rendered content depends on a resolved MFA decision:
  //   - `/verify-mfa` (entire page only makes sense if `mfa_required: true`)
  //   - `/unlock` and `/demo-login` (post-confirm flow branches on the new
  //     session's MFA state)
  const isMfaSensitiveRoute = isOnVerifyMfa || isOnUnlock || isOnDemoLogin;

  // Block rendering whenever auth state is still being resolved AND the
  // current route actually needs it:
  //   - SSO exchange in progress (token may not exist yet), OR
  //   - protected route with token + queries still loading, OR
  //   - MFA-sensitive public route (verify-mfa / unlock) with token loading.
  // Public auth pages (/login, /sign-up, /forgot-password, invitation) render
  // immediately even with a stale token — GuestOnlyRoute will redirect them
  // away once the user query lands, and AppGate then re-evaluates under the
  // protected branch. This avoids a full-screen spinner on /login for users
  // arriving with a stale localStorage token.
  const shouldBlock =
    ssoStatus === "pending" ||
    (!!token &&
      isAuthLoading &&
      (meta.auth === "protected" || isMfaSensitiveRoute));

  if (shouldBlock) {
    return <FullscreenLoader />;
  }

  // Protected-route auth guard — synchronous so the source route never
  // paints before the redirect commits, AND so React Strict Mode's
  // double-invocation of effects in development cannot cause a duplicate
  // `nav('/login')` call. Capturing `from` lets GuestOnlyRoute return the
  // user to the original deep link after a successful login.
  // `alliaiapp` is excluded: that flow is governed by the SSO blocking /
  // denied UI above and must not be force-redirected to /login.
  if (meta.auth === "protected" && !token && !alliaiapp) {
    return (
      <Navigate to={routes.auth.login()} replace state={{ from: location }} />
    );
  }

  // MFA gating runs synchronously in render (not via useEffect) so the
  // source route never paints before the redirect commits.
  // Only enforced once auth has resolved — `mfaRequired` defaults to `true`
  // while the session query is in flight, and firing this gate during that
  // window would force a `/verify-mfa` flash on public routes (like /login)
  // for users without MFA. `/unlock` is exempt so a locked-out user can
  // still recover.
  // MFA wins over the team guard below: a user who must verify MFA cannot
  // be allowed to detour through `/select-team` first.
  if (
    !isAuthLoading &&
    token &&
    mfaRequired &&
    !isOnVerifyMfa &&
    !isOnUnlock &&
    !isOnDemoLogin &&
    !isOnActivateAccount
  ) {
    // Preserve the destination so post-verify navigation lands the user on
    // the page they were trying to reach instead of always bouncing to home.
    // Only capture `from` on protected routes — public auth pages
    // (/login, /sign-up, etc.) are never a meaningful post-verify target.
    const fromState =
      meta.auth === "protected" ? { from: location } : undefined;

    return <Navigate to={routes.verifyMfa()} replace state={fromState} />;
  }
  if (token && !mfaRequired && isOnVerifyMfa) {
    // Honour the captured destination if it looks sane; fall back to home.
    const safeFrom = readSafeFrom(location.state);
    const destination = safeFrom
      ? `${safeFrom.pathname}${safeFrom.search}`
      : routes.home();

    return <Navigate to={destination} replace />;
  }
  // `/verify-mfa` makes no sense without a token — a tokenless user has
  // nothing to verify against. Bounce them to /login.
  if (!token && isOnVerifyMfa) {
    return <Navigate to={routes.auth.login()} replace />;
  }

  // Team-required routes: once the user has resolved AND MFA (if any) is
  // satisfied, redirect to the team-picker when there is no active team.
  // Placed after the MFA gate intentionally — see comment on the MFA block.
  // `user !== null` ensures we never redirect on stale (pre-fetch) data;
  // `shouldBlock` already covers the pre-fetch window for protected routes.
  if (
    meta.auth === "protected" &&
    meta.team === "required" &&
    user !== null &&
    user.current_team_id === null
  ) {
    return <Navigate to={routes.selectTeam()} replace />;
  }

  return <Outlet />;
}
